import React, { useState, useRef, useContext, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import classNames from 'classnames';
import { motion } from 'framer-motion';

import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import useWindowWidthTitles from '../../hooks/useWindowWidthTitles';

import { GradientContext } from '../../containers/BasePage';

import ButtonPrimaryEllipse from '../ButtonPrimaryEllipse/ButtonPrimaryEllipse';
import SlideShowNav from '../SlideShowNav/SlideShowNav';
import EmblaCarousel from '../EmblaCarousel/EmblaCarousel';

import s from './RelatedContent.module.scss';
import PhotoCred from '../PhotoCred/PhotoCred';
import getFocalImageStyles from '../../utils/getFocalImageStyles';

const RelatedContent = ({
  titleTop,
  titleBottom,
  items = [],
  backgroundColor,
  isArticlesListPage = false,
  isDesktop,
  showLinkText = false,
  anchorLink,
  componentModifiers = [],
  nextColor,
}) => {
  const [index, setIndex] = useState(0);
  const [emblaApi, setEmblaApi] = useState(null);
  const [isHovered, setIsHovered] = useState([]);

  const itemRefs = useRef([]);

  const gradient = useContext(GradientContext);
  const { t } = useTranslation();
  const [ref, isVisible] = useIntersectionObserver(0, undefined, true);

  const width = useWindowWidthTitles(isDesktop);
  const sameBackground = nextColor === backgroundColor;

  const isMobile = width < 768;
  const useSlider = isArticlesListPage
    ? false
    : isMobile
      ? items.length > 1
      : items.length > 3;

  const rootClasses = classNames(
    s.Root,
    s[gradient],
    {
      [s.Visible]: isVisible,
      [s.White]: backgroundColor !== 'black',
      [s.Black]: backgroundColor === 'black',
      [s.ArticlesListPage]: isArticlesListPage,
      [s.SameBackground]: sameBackground,
    },
    componentModifiers.map((m) => s[m])
  );

  const titleClasses = classNames(s.Title, { [s.Visible]: isVisible });
  const titleMobileClasses = classNames(s.TitleMobile, {
    [s.Visible]: isVisible,
  });

  const renderItem = (item, i) => {
    const isExternal = item?.link?.isExternal || false;
    const isSingle = items.length === 1;

    const variants = {
      hover: { y: -10, transition: { duration: 0.3 } },
      initial: { y: 0, transition: { duration: 0.3 } },
      initialImage: { opacity: 0, y: -40 },
      animateImage: {
        opacity: 1,
        y: 0,
        transition: { duration: 1, delay: i * 0.15 + 0.4 },
      },
      initialText: { opacity: 0, y: 40 },
      animateText: {
        opacity: 1,
        y: 0,
        transition: { duration: 1, delay: i * 0.15 + 0.5 },
      },
    };

    const cardClass = classNames(s.Item, {
      [s.Single]: items.length === 1,
      [s.Double]: items.length === 2,
      [s.Triple]: items.length === 3,
    });

    return (
      <div
        className={cardClass}
        key={i}
        ref={(el) => (itemRefs.current[i] = el)}
        onMouseEnter={() => setIsHovered([true, i])}
        onMouseLeave={() => setIsHovered([false, i])}>
        <a href={item?.link?.href} className={s.LinkWrapper}></a>
        <motion.span
          className={s.ImageContainer}
          variants={isSingle ? undefined : variants}
          initial={isSingle ? false : 'initialImage'}
          animate={
            isSingle ? false : isVisible ? 'animateImage' : 'initialImage'
          }>
          <Image
            className={s.Image}
            src={item?.image?.url}
            alt={item?.image?.alt || item?.title}
            title={item?.image?.title}
            fill
            draggable={false}
            quality={70}
            style={getFocalImageStyles(item?.image)}
          />
          <PhotoCred credit={item?.image?.credit} modifiers={['Overlay']} />
        </motion.span>

        <motion.div
          className={`${s.Content} black-glass`}
          variants={isSingle ? undefined : variants}
          initial={isSingle ? false : 'initialText'}
          animate={
            isSingle ? false : isVisible ? 'animateText' : 'initialText'
          }>
          <h3 className={s.ItemTitle}>{item?.title}</h3>
          <div className={s.ItemText}>{item?.text}</div>

          <div className={s.ButtonContainer}>
            <ButtonPrimaryEllipse
              animateIcon
              isHovered={isHovered[0] && isHovered[1] === i}
              icon={
                isExternal ? 'icon-external-white' : 'icon-arrow-right-white'
              }>
              {item?.link?.text || t('Common.readMore')}
              <span className="sr-only">{item?.title}</span>
            </ButtonPrimaryEllipse>
          </div>
        </motion.div>
      </div>
    );
  };

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => emblaApi?.off('select', onSelect);
  }, [emblaApi]);

  return (
    <section
      className={rootClasses}
      ref={ref}
      id={anchorLink || 'RelatedContent'}>
      <div className={s.Container}>
        {titleTop &&
          (isMobile ? (
            <h2 className={titleMobileClasses}>
              <span className={titleClasses}>
                <span className={s.TitleTop}>{titleTop}</span>
                <span className={s.TitleBottom}>{titleBottom}</span>
              </span>
            </h2>
          ) : (
            <h2
              className={classNames(s.TitleDesktop, {
                [s.SingleTitle]: items.length === 1,
              })}>
              <span className={titleClasses}>
                <span className={s.TitleTop}>{titleTop}</span>
                <span className={s.TitleBottom}>{titleBottom}</span>
              </span>
            </h2>
          ))}

        {isArticlesListPage && (
          <div className={classNames(s.ArticlesListWrapper)}>
            <div className={s.ArticlesListGrid}>
              <div className={s.ArticlesListItems}>{items.map(renderItem)}</div>
            </div>
          </div>
        )}

        {!isArticlesListPage && useSlider && (
          <div className={s.SliderContainer}>
            <EmblaCarousel
              onSlideChange={(i) => setIndex(i)}
              options={{
                align: 'start',
                containScroll: 'trimSnaps',
                dragFree: false,
              }}
              setApi={setEmblaApi}>
              {items.map(renderItem)}
            </EmblaCarousel>

            <SlideShowNav
              indicators={emblaApi?.scrollSnapList() || []}
              indicatorIndex={index}
              setIndicatorIndex={(i) => {
                setIndex(i);
                emblaApi?.scrollTo(i);
              }}
              isVisible={isVisible}
              color={'black'}
              emblaApi={emblaApi}
            />
          </div>
        )}

        {!isArticlesListPage && !useSlider && (
          <div className={s.ItemContainer}>
            <div className={s.ThreeItems}>{items.map(renderItem)}</div>
          </div>
        )}
      </div>
    </section>
  );
};

export default RelatedContent;
